import {
  gameSlots,
  isBreak,
  resolveSlot,
  type FieldId,
  type GameLabel,
  type GameStats,
  type LabelCtx,
  type PointEvent,
  type ScheduledGame,
  type Slot,
  type SlotDescriptor,
  slotDescriptor,
  type Team,
} from "./types";
import { parseLocal, timeKey } from "./time";

/**
 * What one TEAM's own view of the tournament is made of.
 *
 * The captain's hub (`/team`) answers three questions the public board answers
 * for everybody at once: when do we play, how did we do, and who was on the
 * sheet. Every one of them is derivable from data that is already public — the
 * schedule, its results and the per-game stats snapshots — so nothing here
 * needs a credential or a new endpoint; it needs the tournament-wide shapes
 * folded onto ONE team.
 *
 * Pure, and it stays that way: this is imported by the hub, and the same
 * numbers appear on the board and in the admin, so a rule that lives here can
 * never disagree with itself in two places.
 *
 * Like the rest of `shared/`, it speaks STRUCTURE and not words: an unresolved
 * opponent comes back as a `SlotDescriptor` for `src/i18n/labels.ts` to render,
 * because "Winner Semifinal 1" has to be sayable in both languages and nothing
 * stored carries one.
 */

interface SlotCtx {
  teams: Team[];
  schedule: ScheduledGame[];
  seedsOf: (divisionId: string, poolId?: string) => string[];
}

/** How a game ended, FROM THIS TEAM'S SIDE. Null while it has no result. */
export type TeamOutcome = "win" | "loss" | "draw";

export interface TeamGameRow {
  gameId: string;
  startAt: string;
  fieldId: FieldId;
  /** Which side of the stored row this team is: the snapshot and the result
   *  are both stored as A/B, so everything below has to know which one is us. */
  side: "a" | "b";
  /** The concrete opponent, when one has resolved. */
  opponentId: string | null;
  opponentName: string | null;
  /** What the other side IS when it has not resolved to a team yet — a seed,
   *  the winner of another game, or nothing at all. Words are the client's. */
  opponentSlot: SlotDescriptor;
  note?: string;
  label?: GameLabel;
  stage: "pool" | "bracket";
  divisionId?: string;
  /** From this team's side, and absent until a result is recorded. */
  scoreFor: number | null;
  scoreAgainst: number | null;
  outcome: TeamOutcome | null;
  /** A stats snapshot exists, so the point-by-point detail can be opened.
   *  A game can have a result and no snapshot: a score corrected by hand drops
   *  the point list on purpose (`score_set`), and games played before the
   *  snapshot existed have none either. */
  hasDetail: boolean;
}

/**
 * Every game this team is in, EARLIEST FIRST.
 *
 * Chronological because the list is read as a schedule — the same reason
 * `teamObligations` sorts this way. A row is included when the team resolves
 * into one of the two sides; a bye (one side, or no row at all) therefore
 * never appears, and a bracket game the team has qualified for appears as soon
 * as the slot resolves, which is exactly when the captain needs to know.
 */
export function teamGames(input: {
  teamId: string;
  schedule: ScheduledGame[];
  ctx: SlotCtx;
  labelCtx: LabelCtx;
  /** Stats snapshots, to mark which rows have a point-by-point detail. */
  stats?: readonly GameStats[];
}): TeamGameRow[] {
  const { teamId, schedule, ctx, labelCtx, stats = [] } = input;
  const withDetail = new Set(stats.map((s) => s.scheduleGameId));
  const rows: TeamGameRow[] = [];

  for (const game of [...schedule].sort((a, b) => timeKey(a.startAt) - timeKey(b.startAt))) {
    const slots = gameSlots(game);
    const side = sideOfTeam(slots, ctx, teamId);
    if (side === null) continue;

    const theirs = side === "a" ? slots.b : slots.a;
    const opponent = resolveSlot(theirs, ctx);
    const result = game.result;
    const mine = result === undefined ? null : side === "a" ? result.scoreA : result.scoreB;
    const theirScore = result === undefined ? null : side === "a" ? result.scoreB : result.scoreA;

    rows.push({
      gameId: game.id,
      startAt: game.startAt,
      fieldId: game.fieldId,
      side,
      opponentId: opponent?.id ?? null,
      opponentName: opponent?.name ?? null,
      opponentSlot: slotDescriptor(theirs, labelCtx),
      ...(game.note !== undefined ? { note: game.note } : {}),
      ...(game.label !== undefined ? { label: game.label } : {}),
      stage: game.stage ?? "pool",
      ...(game.divisionId !== undefined ? { divisionId: game.divisionId } : {}),
      scoreFor: mine,
      scoreAgainst: theirScore,
      outcome: outcomeOf(mine, theirScore),
      hasDetail: withDetail.has(game.id),
    });
  }
  return rows;
}

/** Which side this team is on, or null when it is not in the game at all. */
function sideOfTeam(
  slots: { a: Slot | null; b: Slot | null },
  ctx: SlotCtx,
  teamId: string,
): "a" | "b" | null {
  if (resolveSlot(slots.a, ctx)?.id === teamId) return "a";
  if (resolveSlot(slots.b, ctx)?.id === teamId) return "b";
  return null;
}

function outcomeOf(mine: number | null, theirs: number | null): TeamOutcome | null {
  if (mine === null || theirs === null) return null;
  if (mine === theirs) return "draw";
  return mine > theirs ? "win" : "loss";
}

export interface TeamRecord {
  played: number;
  won: number;
  lost: number;
  drawn: number;
  pointsFor: number;
  pointsAgainst: number;
  /** Goal difference, the column every ultimate standings table carries. */
  diff: number;
}

/** The team's record over the rows it has actually played. */
export function teamRecord(rows: readonly TeamGameRow[]): TeamRecord {
  const out: TeamRecord = {
    played: 0,
    won: 0,
    lost: 0,
    drawn: 0,
    pointsFor: 0,
    pointsAgainst: 0,
    diff: 0,
  };
  for (const row of rows) {
    if (row.outcome === null || row.scoreFor === null || row.scoreAgainst === null) continue;
    out.played += 1;
    if (row.outcome === "win") out.won += 1;
    else if (row.outcome === "loss") out.lost += 1;
    else out.drawn += 1;
    out.pointsFor += row.scoreFor;
    out.pointsAgainst += row.scoreAgainst;
  }
  out.diff = out.pointsFor - out.pointsAgainst;
  return out;
}

/**
 * The next game to turn up to: the earliest row with no result yet.
 *
 * Deliberately NOT "the earliest row that starts after now". A game whose
 * scheduled time has passed but which has no result is the one a captain is
 * standing at the pitch for — a schedule that has slipped twenty minutes must
 * not skip straight past it to the following round.
 */
export function nextGame(rows: readonly TeamGameRow[], now: number): TeamGameRow | null {
  for (const row of rows) {
    if (row.outcome !== null) continue;
    const at = parseLocal(row.startAt)?.getTime();
    // A row whose time will not parse still counts: it is unplayed, and
    // hiding it would be the one failure mode worth avoiding here.
    if (at === undefined || at + STALE_MS >= now) return row;
  }
  return null;
}

/**
 * How long after its start time an unplayed game stops being "next".
 *
 * Two hours: longer than any game plus the turnaround, so a result nobody
 * recorded does not pin the banner to a game that finished this morning, and
 * short enough that a game running late is still the one being pointed at.
 */
const STALE_MS = 2 * 60 * 60_000;

// ---------- one played game, point by point ----------

export interface MatchPoint {
  /** 1-based, so the client never renders an index off an array position. */
  n: number;
  /** Scored by us. */
  ours: boolean;
  scoreFor: number;
  scoreAgainst: number;
  /** Names come from the SNAPSHOT's own roster copy, never today's roster: a
   *  later roster edit must not rewrite who scored in a played game. */
  scorer: string | null;
  assist: string | null;
  /** Won by the team that started the point on defence. */
  isBreak: boolean;
}

export interface MatchLeader {
  playerId: string;
  name: string;
  number: string;
  goals: number;
  assists: number;
  /** Goals and assists that came on a break, the closest this data gets to
   *  defence: turnovers and blocks are not recorded anywhere. */
  breaks: number;
}

export interface MatchDetail {
  gameId: string;
  side: "A" | "B";
  teamName: string;
  teamShort: string;
  opponentName: string;
  opponentShort: string;
  /** The RECORDED score, from the schedule row, when the caller passed it.
   *  Falls back to the snapshot's own copy only when it did not. */
  scoreFor: number;
  scoreAgainst: number;
  /** Whether `points` actually adds up to the score above.
   *
   *  False means the two disagree, and then the point list, the break totals
   *  and the scorers below describe the game as it stood BEFORE the score was
   *  changed. Still a real record, and still worth reading — but a caller that
   *  prints it unmarked prints a game that no longer exists. Empty point lists
   *  are `true`: an absent record is not a contradiction, the same rule
   *  `needsReconciliation` uses in the editor. */
  reconciled: boolean;
  /** What the point list below adds up to, from this team's side.
   *
   *  Equal to `scoreFor`/`scoreAgainst` whenever `reconciled`. When it is not,
   *  this is the score the list belongs to, which is the number to show beside
   *  it: counted off the points themselves, so it can never disagree with the
   *  list a reader is looking at. (`pointsFor` and `total` are the same count
   *  seen twice: one as half a scoreline, one as a coverage denominator.) */
  pointsFor: number;
  pointsAgainst: number;
  points: MatchPoint[];
  /** Points of ours that carry a scorer, out of all of ours. The volunteer's
   *  sheet is skippable by design, so every list of scorers is shown next to
   *  how much of the game it actually covers. */
  credited: number;
  total: number;
  breaksFor: number;
  breaksAgainst: number;
  leaders: MatchLeader[];
  recordedAt: number;
}

/**
 * One finished game, from this team's side.
 *
 * `null` when the snapshot does not belong to this team, which is the honest
 * answer for a stale link or a game whose sides were rewired after it was
 * played: the snapshot froze the teams as they were, and re-deriving them now
 * would put one team's points under another team's name.
 *
 * ## Where the SCORE comes from
 *
 * From `recorded` — the schedule row's result, already turned around to this
 * team's side — and NOT from the snapshot, whenever the caller has it.
 *
 * The two are written together by every path that finishes or corrects a game
 * (`recordResult`, `applyCorrection`), but the schedule row can also be
 * written on its own (`PUT /api/schedule/:id`, `POST /api/schedule/bulk`),
 * and then the snapshot's copy is stale. The recorded result is the one the
 * standings count, the one `resolveSlot` sends up the bracket and the one the
 * result card prints, so it is the one a team's own page has to agree with:
 * reading the snapshot's copy here is what let a won game carry a "Lost"
 * badge on the very screen that had just listed it as a win.
 *
 * `recorded` is optional so the shape stays usable without a schedule to hand;
 * without it the snapshot's own copy is all there is, which is the same answer
 * as before rather than no answer.
 */
export function matchDetail(
  gs: GameStats,
  teamId: string,
  recorded?: { scoreFor: number; scoreAgainst: number } | null,
): MatchDetail | null {
  const side: "A" | "B" | null =
    gs.teamA.teamId === teamId ? "A" : gs.teamB.teamId === teamId ? "B" : null;
  if (side === null) return null;

  const us = side === "A" ? gs.teamA : gs.teamB;
  const them = side === "A" ? gs.teamB : gs.teamA;
  const roster = us.roster ?? [];
  const nameOf = (id: string | null): string | null =>
    id === null ? null : (roster.find((p) => p.id === id)?.name ?? null);

  const points: MatchPoint[] = [];
  const leaders = new Map<string, MatchLeader>();
  let credited = 0;
  let total = 0;
  let breaksFor = 0;
  let breaksAgainst = 0;

  for (const [i, point] of gs.points.entries()) {
    const ours = point.team === side;
    const broke = isBreak(point);
    if (ours) {
      total += 1;
      if (point.scorerId !== null) credited += 1;
      if (broke) breaksFor += 1;
      credit(leaders, roster, point, broke);
    } else if (broke) {
      breaksAgainst += 1;
    }
    points.push({
      n: i + 1,
      ours,
      scoreFor: side === "A" ? point.scoreA : point.scoreB,
      scoreAgainst: side === "A" ? point.scoreB : point.scoreA,
      scorer: ours ? nameOf(point.scorerId) : null,
      assist: ours ? nameOf(point.assistId) : null,
      isBreak: broke,
    });
  }

  const scoreFor = recorded?.scoreFor ?? (side === "A" ? gs.scoreA : gs.scoreB);
  const scoreAgainst = recorded?.scoreAgainst ?? (side === "A" ? gs.scoreB : gs.scoreA);
  // Counted off the points themselves rather than off the snapshot's score:
  // the question is whether THIS list adds up to the score being published
  // above it, and the snapshot's copy is the value under suspicion.
  const ourPoints = points.filter((p) => p.ours).length;
  const theirPoints = points.length - ourPoints;

  return {
    gameId: gs.scheduleGameId,
    side,
    teamName: us.name,
    teamShort: us.short,
    opponentName: them.name,
    opponentShort: them.short,
    scoreFor,
    scoreAgainst,
    reconciled: points.length === 0 || (ourPoints === scoreFor && theirPoints === scoreAgainst),
    pointsFor: ourPoints,
    pointsAgainst: theirPoints,
    points,
    credited,
    total,
    breaksFor,
    breaksAgainst,
    // Goals first, then assists: the same order every other table in this app
    // ranks by, and ties stay ties.
    leaders: [...leaders.values()].sort(
      (a, b) => b.goals - a.goals || b.assists - a.assists || a.name.localeCompare(b.name),
    ),
    recordedAt: gs.recordedAt,
  };
}

/** Fold one of our points into the per-player tallies. */
function credit(
  leaders: Map<string, MatchLeader>,
  roster: readonly { id: string; name: string; number: string }[],
  point: PointEvent,
  broke: boolean,
): void {
  const bump = (id: string | null, what: "goals" | "assists"): void => {
    if (id === null) return;
    const player = roster.find((p) => p.id === id);
    // A credit pointing at somebody who is not on the snapshot's roster is
    // dropped rather than shown as a blank row: the snapshot is the record of
    // who played, and inventing a name from an id helps nobody.
    if (player === undefined) return;
    const row = leaders.get(id) ?? {
      playerId: id,
      name: player.name,
      number: player.number,
      goals: 0,
      assists: 0,
      breaks: 0,
    };
    row[what] += 1;
    if (broke) row.breaks += 1;
    leaders.set(id, row);
  };
  bump(point.scorerId, "goals");
  bump(point.assistId, "assists");
}
